import clsx from 'clsx';
import {
  ArrowLeft,
  Award,
  CalendarDays,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Upload,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCsvExport } from '../csvExport';
import {
  type GoldenPreviewResponse,
  type GoldenResultsCsvView,
  type GoldenResultsRange,
  type GoldenSetDetail,
  type GoldenSetResults,
  type GoldenSetStatus,
  type GoldenSetSummary,
  getGoldenSet,
  getGoldenSetResults,
  goldenSetResultsCsvPath,
  listGoldenOperators,
  listGoldenSets,
  parseEpisodeIdsFromCsv,
  parseEpisodeIdsFromText,
  previewGoldenSet,
  publishGoldenSet,
  releaseGoldenSetFeedback,
  setGoldenSetStatus,
} from '../golden';
import { useStore } from '../store';
import { showToast } from '../toast';
import { CsvExportProgress } from './CsvExportProgress';
import { ParticipantPicker } from './ParticipantPicker';

type View = 'list' | 'create' | 'detail';
type RangeMode = 'all' | 'range';
type ResultView = GoldenResultsCsvView;

function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString();
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function todayDateInputValue(): string {
  return toDateInputValue(new Date());
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function parseDateInput(date: string): Date | null {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatCalendarLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatRangeButtonLabel(
  fromDate: string,
  fromTime: string,
  toDate: string,
  toTime: string
): string {
  const from = fromDate || 'Start date';
  const to = toDate || 'End date';
  const fromSuffix = fromTime ? ` ${fromTime}` : '';
  const toSuffix = toTime ? ` ${toTime}` : '';
  return `${from}${fromSuffix} - ${to}${toSuffix}`;
}

function localDateTimeMs(date: string, time: string, isEnd: boolean): number {
  if (!time) {
    const value = new Date(`${date}T00:00:00`);
    if (isEnd) value.setDate(value.getDate() + 1);
    return value.getTime();
  }
  const value = new Date(`${date}T${time}`);
  if (isEnd) value.setMinutes(value.getMinutes() + 1);
  return value.getTime();
}

function dateTimeFilenamePart(date: string, time: string): string {
  if (!time) return date;
  return `${date}T${time.replace(':', '')}`;
}

function resolveResultsRange(
  mode: RangeMode,
  fromDate: string,
  fromTime: string,
  toDate: string,
  toTime: string
): { range: GoldenResultsRange | null; error: string; filenamePart: string } {
  if (mode === 'all') return { range: null, error: '', filenamePart: 'all-time' };
  if (!fromDate || !toDate) {
    return { range: null, error: '请选择完整的开始和结束日期。', filenamePart: 'date-range' };
  }

  const fromMs = localDateTimeMs(fromDate, fromTime, false);
  const toMs = localDateTimeMs(toDate, toTime, true);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) {
    return { range: null, error: '日期或时间格式不正确。', filenamePart: 'date-range' };
  }
  if (toMs <= fromMs) {
    return { range: null, error: '结束时间必须晚于开始时间。', filenamePart: 'date-range' };
  }

  return {
    range: { fromMs, toMs },
    error: '',
    filenamePart: `${dateTimeFilenamePart(fromDate, fromTime)}_${dateTimeFilenamePart(
      toDate,
      toTime
    )}`,
  };
}

function statusBadgeClass(status: GoldenSetStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40';
    case 'paused':
      return 'bg-amber-500/10 text-amber-300 border-amber-500/40';
    case 'closed':
      return 'bg-zinc-500/10 text-zinc-300 border-zinc-500/40';
    case 'archived':
      return 'bg-zinc-700/40 text-zinc-400 border-zinc-700/60';
    default:
      return 'bg-blue-500/10 text-blue-300 border-blue-500/40';
  }
}

export function GoldenAdminPanel() {
  const { isAdmin, showDashboard } = useStore();
  const [view, setView] = useState<View>('list');
  const [sets, setSets] = useState<GoldenSetSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listGoldenSets();
      setSets(data);
    } catch (err) {
      console.error('Failed to load golden sets', err);
      showToast('Failed to load golden sets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md py-32 text-center text-brand-text-secondary">
        Admin only.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-brand-border pb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <Award className="h-8 w-8 text-brand-orange" />
            Golden Set
          </h1>
          <p className="mt-2 text-brand-text-secondary text-sm">
            Hidden calibration. Operators receive these episodes through their normal task queue
            with no visible indicator. Their answers are scored against admin-curated ground truth.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {view === 'list' && (
            <button
              type="button"
              onClick={() => void refreshList()}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
            >
              <RefreshCw className={clsx('h-3.5 w-3.5', loading && 'animate-spin')} />
              Refresh
            </button>
          )}
          {view !== 'list' && (
            <button
              type="button"
              onClick={() => {
                setView('list');
                setSelectedSetId(null);
                void refreshList();
              }}
              className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sets
            </button>
          )}
          <button
            type="button"
            onClick={() => void showDashboard()}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-4 py-2 text-sm font-medium text-brand-text-secondary transition hover:border-brand-text-secondary/50 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
        </div>
      </div>

      {view === 'list' && (
        <SetListView
          sets={sets}
          loading={loading}
          onCreate={() => setView('create')}
          onOpen={(setId) => {
            setSelectedSetId(setId);
            setView('detail');
          }}
        />
      )}
      {view === 'create' && (
        <CreateSetView
          onPublished={() => {
            setView('list');
            void refreshList();
          }}
        />
      )}
      {view === 'detail' && selectedSetId && (
        <SetDetailView setId={selectedSetId} onChange={() => void refreshList()} />
      )}
    </div>
  );
}

// ── List view ────────────────────────────────────────────────────────────

function SetListView({
  sets,
  loading,
  onCreate,
  onOpen,
}: {
  sets: GoldenSetSummary[];
  loading: boolean;
  onCreate: () => void;
  onOpen: (setId: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={onCreate}
          className="flex items-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-brand-orange-hover"
        >
          <Upload className="h-4 w-4" />
          New set
        </button>
      </div>

      <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden">
        {sets.length === 0 ? (
          <div className="p-10 text-center text-sm text-brand-text-secondary">
            {loading ? 'Loading…' : 'No golden sets yet. Create one to start hidden calibration.'}
          </div>
        ) : (
          <div className="divide-y divide-brand-border">
            <div className="grid grid-cols-12 gap-2 bg-brand-surface-highlight/40 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
              <div className="col-span-4">Set</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Episodes</div>
              <div className="col-span-2 text-right">Participants</div>
              <div className="col-span-2 text-right">Completed</div>
            </div>
            {sets.map((s) => (
              <button
                key={s.set_id}
                type="button"
                onClick={() => onOpen(s.set_id)}
                className="grid grid-cols-12 gap-2 px-5 py-3 text-sm text-left w-full hover:bg-brand-surface-highlight/30 transition"
              >
                <div className="col-span-4">
                  <div className="font-medium text-brand-text-primary truncate flex items-center gap-2">
                    {s.name}
                    <ChevronRight className="h-4 w-4 text-brand-text-secondary" />
                  </div>
                  <div className="text-xs text-brand-text-secondary">
                    {fmtDate(s.created_at_ms)} · {s.created_by}
                  </div>
                </div>
                <div className="col-span-2">
                  <span
                    className={clsx(
                      'inline-block rounded border px-2 py-0.5 text-[11px] font-medium',
                      statusBadgeClass(s.status)
                    )}
                  >
                    {s.status}
                  </span>
                </div>
                <div className="col-span-2 text-right font-mono text-brand-text-secondary">
                  {s.episode_count}
                </div>
                <div className="col-span-2 text-right font-mono text-brand-text-secondary">
                  {s.participant_count}
                </div>
                <div className="col-span-2 text-right font-mono text-brand-text-secondary">
                  {s.attempts_completed}/{s.attempts_total}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create view ──────────────────────────────────────────────────────────

function CreateSetView({ onPublished }: { onPublished: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Priority-ordered list of operators whose existing audits/votes may be used
  // as ground truth. Per-episode, the backend walks this list in order: first
  // matching audit wins; if none have an audit, first matching vote wins.
  const [groundTruthOperators, setGroundTruthOperators] = useState<string[]>([]);
  const [episodeIdsText, setEpisodeIdsText] = useState('');
  const [parsedIds, setParsedIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<GoldenPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [operators, setOperators] = useState<string[]>([]);
  const [selectedOperators, setSelectedOperators] = useState<Set<string>>(new Set());
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const ops = await listGoldenOperators();
        setOperators(ops);
      } catch (err) {
        console.error('Failed to load operators', err);
      }
    })();
  }, []);

  const groundTruthOperatorSet = useMemo(
    () => new Set(groundTruthOperators),
    [groundTruthOperators]
  );

  // The picker stores selection as a Set; we project it back into the ordered
  // list so the priority order persists. Newly checked names append; unchecked
  // names drop. This keeps the order stable when the user toggles a single op.
  const handleGroundTruthChange = (next: Set<string>) => {
    const kept = groundTruthOperators.filter((op) => next.has(op));
    const added = Array.from(next).filter((op) => !kept.includes(op));
    setGroundTruthOperators([...kept, ...added]);
    setPreview(null);
  };

  const moveGroundTruth = (op: string, dir: -1 | 1) => {
    setGroundTruthOperators((curr) => {
      const idx = curr.indexOf(op);
      const next = idx + dir;
      if (idx < 0 || next < 0 || next >= curr.length) return curr;
      const arr = [...curr];
      [arr[idx], arr[next]] = [arr[next]!, arr[idx]!];
      return arr;
    });
    setPreview(null);
  };

  // Filter out any GT operator that snuck into the participants set (e.g. the
  // user picked X as a participant, then later promoted X to GT). The backend
  // does this filter too — we mirror it client-side so the UI stays honest.
  const effectiveParticipants = useMemo(
    () => new Set(Array.from(selectedOperators).filter((op) => !groundTruthOperatorSet.has(op))),
    [selectedOperators, groundTruthOperatorSet]
  );

  const handleFile = async (file: File) => {
    const text = await file.text();
    const ids = parseEpisodeIdsFromCsv(text);
    setEpisodeIdsText(ids.join('\n'));
    setParsedIds(ids);
    setPreview(null);
  };

  const handleParseText = () => {
    const ids = parseEpisodeIdsFromText(episodeIdsText);
    setParsedIds(ids);
    setPreview(null);
  };

  const handlePreview = async () => {
    const ids = parsedIds.length > 0 ? parsedIds : parseEpisodeIdsFromText(episodeIdsText);
    if (ids.length === 0) {
      showToast('Add at least one episode_id');
      return;
    }
    if (groundTruthOperators.length === 0) {
      showToast('Pick at least one ground-truth operator');
      return;
    }
    setParsedIds(ids);
    setPreviewLoading(true);
    try {
      const result = await previewGoldenSet(ids, groundTruthOperators);
      setPreview(result);
    } catch (err) {
      console.error('preview failed', err);
      showToast('Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!preview || preview.valid.length === 0) {
      showToast('Preview the episode list first');
      return;
    }
    if (!name.trim()) {
      showToast('Set name is required');
      return;
    }
    if (groundTruthOperators.length === 0) {
      showToast('Pick at least one ground-truth operator');
      return;
    }
    if (effectiveParticipants.size === 0) {
      showToast('Pick at least one participant');
      return;
    }
    setPublishing(true);
    try {
      const result = await publishGoldenSet({
        name: name.trim(),
        description: description.trim() || null,
        target_ratio: null,
        max_per_operator_per_day: null,
        ground_truth_operators: groundTruthOperators,
        episode_ids: preview.valid.map((r) => r.episode_id),
        participants: Array.from(effectiveParticipants),
      });
      if (!result?.set_id) {
        showToast('Publish failed');
        return;
      }
      showToast(
        `Published ${result.valid_episodes} ep × ${result.participants} ops = ${result.attempts_created} attempts`
      );
      onPublished();
    } catch (err) {
      console.error('publish failed', err);
      showToast('Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-brand-border bg-brand-surface p-5 space-y-4">
        <h2 className="text-sm font-semibold text-brand-text-primary">1 · Set metadata</h2>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Set name (e.g. 2026-04 calibration round 1)"
          className="h-9 w-full rounded-lg border border-brand-border bg-brand-dark px-3 text-sm text-brand-text-primary focus:border-brand-orange focus:outline-none"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description / notes for admins"
          className="w-full min-h-[60px] rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-sm text-brand-text-primary focus:border-brand-orange focus:outline-none"
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-brand-text-secondary uppercase tracking-wider">
              Ground-truth operators
            </span>
            <ParticipantPicker
              operators={operators}
              selected={groundTruthOperatorSet}
              onChange={handleGroundTruthChange}
              buttonLabel={(n) =>
                n === 0
                  ? 'Pick ground-truth operators…'
                  : `${n} ground-truth operator${n !== 1 ? 's' : ''} selected`
              }
              title="Ground-truth operators"
              emptyMessage="No operators available."
            />
          </div>
          {groundTruthOperators.length === 0 ? (
            <p className="text-xs text-brand-text-secondary">
              Pick the operator(s) whose existing answers serve as ground truth. Per-episode, the
              backend tries each in order: first matching audit wins; if none, first matching vote
              wins.
            </p>
          ) : (
            <ol className="space-y-1 text-xs">
              {groundTruthOperators.map((op, idx) => (
                <li
                  key={op}
                  className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-dark px-3 py-1.5"
                >
                  <span className="font-mono text-brand-text-secondary w-4 text-right">
                    {idx + 1}.
                  </span>
                  <span className="flex-1 font-mono text-brand-text-primary truncate">{op}</span>
                  <button
                    type="button"
                    onClick={() => moveGroundTruth(op, -1)}
                    disabled={idx === 0}
                    className="text-brand-text-secondary hover:text-white disabled:opacity-30"
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveGroundTruth(op, 1)}
                    disabled={idx === groundTruthOperators.length - 1}
                    className="text-brand-text-secondary hover:text-white disabled:opacity-30"
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next = new Set(groundTruthOperatorSet);
                      next.delete(op);
                      handleGroundTruthChange(next);
                    }}
                    className="text-brand-text-secondary hover:text-red-400"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-brand-border bg-brand-surface p-5 space-y-3">
        <h2 className="text-sm font-semibold text-brand-text-primary">2 · Episodes</h2>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs text-brand-text-secondary cursor-pointer hover:text-white">
            <Upload className="h-3.5 w-3.5" />
            <span>Upload CSV</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
          </label>
          <span className="text-xs text-brand-text-secondary">
            …or paste episode_id values below (whitespace, comma, or semicolon separated).
          </span>
        </div>
        <textarea
          value={episodeIdsText}
          onChange={(e) => setEpisodeIdsText(e.target.value)}
          onBlur={handleParseText}
          placeholder="episode_id_1\nepisode_id_2"
          className="w-full min-h-[120px] rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs font-mono text-brand-text-primary focus:border-brand-orange focus:outline-none"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-brand-text-secondary">
            {parsedIds.length > 0 ? `${parsedIds.length} unique IDs parsed` : 'Not yet parsed'}
          </span>
          <button
            type="button"
            onClick={() => void handlePreview()}
            disabled={previewLoading || groundTruthOperators.length === 0}
            className="rounded-lg border border-brand-border bg-brand-dark px-4 py-2 text-xs font-semibold text-brand-text-primary hover:border-brand-orange hover:text-white disabled:opacity-50"
          >
            {previewLoading ? 'Validating…' : 'Validate against ground truth'}
          </button>
        </div>

        {preview && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="text-xs font-semibold text-emerald-300 uppercase tracking-wider mb-2 flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5" />
                Valid · {preview.valid.length}
              </div>
              <div className="max-h-48 overflow-auto space-y-1 text-xs font-mono text-brand-text-secondary">
                {preview.valid.map((r) => (
                  <div key={r.episode_id} className="flex justify-between">
                    <span className="truncate">{r.episode_id}</span>
                    <span className="text-emerald-400 ml-2 shrink-0">
                      {r.ground_truth_operator} · {r.ground_truth_source}
                    </span>
                  </div>
                ))}
                {preview.valid.length === 0 && <div className="text-brand-text-secondary">—</div>}
              </div>
            </div>
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
              <div className="text-xs font-semibold text-red-300 uppercase tracking-wider mb-2 flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5" />
                Invalid · {preview.invalid.length}
              </div>
              <div className="max-h-48 overflow-auto space-y-1 text-xs font-mono text-brand-text-secondary">
                {preview.invalid.map((r) => (
                  <div key={r.episode_id}>
                    <div className="truncate">{r.episode_id}</div>
                    <div className="text-red-400 text-[11px]">{r.reason}</div>
                  </div>
                ))}
                {preview.invalid.length === 0 && <div className="text-brand-text-secondary">—</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-brand-border bg-brand-surface p-5 space-y-3">
        <h2 className="text-sm font-semibold text-brand-text-primary">3 · Participants</h2>
        <div className="flex items-center gap-3">
          {operators.length === 0 ? (
            <span className="text-xs text-brand-text-secondary">No active operators found.</span>
          ) : (
            <ParticipantPicker
              operators={operators}
              selected={selectedOperators}
              excludedOperators={groundTruthOperatorSet}
              onChange={setSelectedOperators}
            />
          )}
          {groundTruthOperators.length > 0 && effectiveParticipants.size > 0 && (
            <span className="text-xs text-brand-text-secondary">
              GT operator{groundTruthOperators.length !== 1 ? 's' : ''} (
              {groundTruthOperators.join(', ')}) excluded automatically
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handlePublish()}
          disabled={
            publishing ||
            !preview ||
            preview.valid.length === 0 ||
            effectiveParticipants.size === 0 ||
            groundTruthOperators.length === 0
          }
          className="rounded-lg bg-brand-orange px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-brand-orange-hover disabled:opacity-50"
        >
          {publishing ? 'Publishing…' : 'Publish hidden golden set'}
        </button>
      </div>
    </div>
  );
}

// ── Detail view ──────────────────────────────────────────────────────────

type DetailTab = 'overview' | 'results';

function SetDetailView({ setId, onChange }: { setId: string; onChange: () => void }) {
  const [tab, setTab] = useState<DetailTab>('overview');
  const [detail, setDetail] = useState<GoldenSetDetail | null>(null);
  const [results, setResults] = useState<GoldenSetResults | null>(null);
  const [rangeMode, setRangeMode] = useState<RangeMode>('all');
  const [fromDate, setFromDate] = useState(todayDateInputValue);
  const [fromTime, setFromTime] = useState('00:00');
  const [toDate, setToDate] = useState(todayDateInputValue);
  const [toTime, setToTime] = useState('23:59');
  const [resultView, setResultView] = useState<ResultView>('operators');
  const [selectedEpisodeId, setSelectedEpisodeId] = useState('');
  const rangeState = useMemo(
    () => resolveResultsRange(rangeMode, fromDate, fromTime, toDate, toTime),
    [rangeMode, fromDate, fromTime, toDate, toTime]
  );
  const appliedEpisodeId = resultView === 'episodes' ? selectedEpisodeId : '';
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const {
    exportState,
    isExporting: isCsvExporting,
    downloadCsv,
    clearExportState,
  } = useCsvExport();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        getGoldenSet(setId),
        rangeState.error
          ? Promise.resolve(null)
          : getGoldenSetResults(setId, rangeState.range, appliedEpisodeId || null),
      ]);
      setDetail(d);
      setResults(r);
    } catch (err) {
      console.error('Failed to load set detail', err);
      showToast('Failed to load set');
    } finally {
      setLoading(false);
    }
  }, [appliedEpisodeId, setId, rangeState.error, rangeState.range]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectableEpisodeIds = useMemo(
    () =>
      results?.episodes
        .filter((episode) => episode.total_attempts > 0 || episode.completed_attempts > 0)
        .map((episode) => episode.episode_id) ?? [],
    [results]
  );

  useEffect(() => {
    if (resultView !== 'episodes') return;
    if (selectableEpisodeIds.length === 0) {
      if (selectedEpisodeId) setSelectedEpisodeId('');
      return;
    }
    if (!selectedEpisodeId || !selectableEpisodeIds.includes(selectedEpisodeId)) {
      const firstEpisodeId = selectableEpisodeIds[0];
      if (firstEpisodeId) setSelectedEpisodeId(firstEpisodeId);
    }
  }, [resultView, selectableEpisodeIds, selectedEpisodeId]);

  const handleStatus = async (next: GoldenSetStatus) => {
    setBusy(true);
    try {
      await setGoldenSetStatus(setId, next);
      showToast(`Status: ${next}`);
      await refresh();
      onChange();
    } catch (err) {
      console.error('Status change failed', err);
      showToast('Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const handleRelease = async () => {
    if (
      !confirm(
        'Release feedback to participants?\n\nOperators will be able to see their own answers vs the snapshotted ground truth on the quality feedback page. Future hidden assignments in this set become traceable to operators who paid attention. This cannot be undone.'
      )
    )
      return;
    setBusy(true);
    try {
      const res = await releaseGoldenSetFeedback(setId);
      if (!res?.ok) {
        showToast('Release failed');
        return;
      }
      showToast('Feedback released');
      await refresh();
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const handleClearRangeInputs = () => {
    setFromDate('');
    setFromTime('');
    setToDate('');
    setToTime('');
  };

  const handleExportResults = async () => {
    if (rangeState.error) {
      showToast(rangeState.error);
      return;
    }
    const exportEpisodeId = resultView === 'episodes' ? selectedEpisodeId : '';
    if (resultView === 'episodes' && !exportEpisodeId) {
      showToast('No episode selected');
      return;
    }
    const filename = exportEpisodeId
      ? `golden_episode_${exportEpisodeId}_${setId}_${rangeState.filenamePart}.csv`
      : `golden_${resultView}_${setId}_${rangeState.filenamePart}.csv`;
    try {
      await downloadCsv({
        path: goldenSetResultsCsvPath(setId, resultView, rangeState.range, exportEpisodeId || null),
        filename,
        label: `Golden ${resultView}`,
      });
      showToast('Golden results exported');
    } catch (err) {
      console.error('Golden results export failed', err);
      showToast('Export failed');
    } finally {
      setTimeout(clearExportState, 1800);
    }
  };

  const totalAttempts = useMemo(() => {
    if (!detail) return 0;
    return Object.values(detail.attempts_by_status).reduce((a, b) => a + b, 0);
  }, [detail]);

  if (loading || !detail) {
    return (
      <div className="text-center py-16 text-brand-text-secondary">
        {loading ? 'Loading…' : 'Set not found.'}
      </div>
    );
  }

  const { set, episodes, participants, attempts_by_status } = detail;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-brand-border bg-brand-surface p-5 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-brand-text-primary">{set.name}</h2>
            {set.description && (
              <p className="text-sm text-brand-text-secondary mt-1">{set.description}</p>
            )}
            <div className="mt-2 text-xs text-brand-text-secondary">
              created {fmtDate(set.created_at_ms)} by {set.created_by}
              {set.published_at_ms && <> · published {fmtDate(set.published_at_ms)}</>}
              {set.closed_at_ms && <> · closed {fmtDate(set.closed_at_ms)}</>}
            </div>
          </div>
          <span
            className={clsx(
              'inline-block rounded border px-3 py-1 text-xs font-semibold',
              statusBadgeClass(set.status)
            )}
          >
            {set.status}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-brand-border">
          {set.status === 'active' && (
            <button
              type="button"
              onClick={() => void handleStatus('paused')}
              disabled={busy}
              className="flex items-center gap-1 rounded-lg border border-brand-border bg-brand-dark px-3 py-1.5 text-xs text-brand-text-secondary hover:text-white"
            >
              <PauseCircle className="h-3.5 w-3.5" /> Pause
            </button>
          )}
          {set.status === 'paused' && (
            <button
              type="button"
              onClick={() => void handleStatus('active')}
              disabled={busy}
              className="flex items-center gap-1 rounded-lg border border-brand-border bg-brand-dark px-3 py-1.5 text-xs text-brand-text-secondary hover:text-white"
            >
              <PlayCircle className="h-3.5 w-3.5" /> Resume
            </button>
          )}
          {(set.status === 'active' || set.status === 'paused') && (
            <button
              type="button"
              onClick={() => void handleStatus('closed')}
              disabled={busy}
              className="flex items-center gap-1 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/20"
            >
              <XCircle className="h-3.5 w-3.5" /> Close
            </button>
          )}
          {set.feedback_released_at_ms == null ? (
            <button
              type="button"
              onClick={() => void handleRelease()}
              disabled={busy}
              className="flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/20"
            >
              <CheckCircle className="h-3.5 w-3.5" /> Release feedback
            </button>
          ) : (
            <span className="flex items-center gap-1 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-xs text-emerald-400">
              <CheckCircle className="h-3.5 w-3.5" /> Released{' '}
              {fmtDate(set.feedback_released_at_ms)}
            </span>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-brand-border">
        {(
          [
            { id: 'overview', label: 'Overview' },
            { id: 'results', label: 'Results' },
          ] satisfies Array<{ id: DetailTab; label: string }>
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition',
              tab === t.id
                ? 'border-brand-orange text-brand-text-primary'
                : 'border-transparent text-brand-text-secondary hover:text-white'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="grid gap-4 sm:grid-cols-4">
            <Stat label="Episodes" value={episodes.length} />
            <Stat label="Participants" value={participants.length} />
            <Stat label="Attempts" value={totalAttempts} />
            <Stat label="Completed" value={`${attempts_by_status.completed} / ${totalAttempts}`} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden">
              <div className="border-b border-brand-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                Episodes
              </div>
              <div className="max-h-96 overflow-auto divide-y divide-brand-border">
                {episodes.map((ep) => (
                  <div key={ep.episode_id} className="px-4 py-2 text-xs flex justify-between">
                    <span className="font-mono truncate">{ep.episode_id}</span>
                    <span className="text-brand-text-secondary ml-2 shrink-0">
                      {ep.ground_truth_source} · {ep.duration}s
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden">
              <div className="border-b border-brand-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                Participants
              </div>
              <div className="max-h-96 overflow-auto divide-y divide-brand-border">
                {participants.map((p) => (
                  <div key={p.operator} className="px-4 py-2 text-xs flex justify-between">
                    <span className="font-medium">{p.operator}</span>
                    <span className="text-brand-text-secondary ml-2 shrink-0">
                      {p.active ? 'active' : 'inactive'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'results' && (
        <>
          <CsvExportProgress state={exportState} />
          <ResultsView
            results={results}
            loading={loading}
            rangeMode={rangeMode}
            fromDate={fromDate}
            fromTime={fromTime}
            toDate={toDate}
            toTime={toTime}
            rangeError={rangeState.error}
            resultView={resultView}
            selectedEpisodeId={selectedEpisodeId}
            isCsvExporting={isCsvExporting}
            onRangeModeChange={setRangeMode}
            onFromDateChange={setFromDate}
            onFromTimeChange={setFromTime}
            onToDateChange={setToDate}
            onToTimeChange={setToTime}
            onResultViewChange={setResultView}
            onSelectedEpisodeIdChange={setSelectedEpisodeId}
            onClearRangeInputs={handleClearRangeInputs}
            onExport={handleExportResults}
          />
        </>
      )}
    </div>
  );
}

function ResultsView({
  results,
  loading,
  rangeMode,
  fromDate,
  fromTime,
  toDate,
  toTime,
  rangeError,
  resultView,
  selectedEpisodeId,
  isCsvExporting,
  onRangeModeChange,
  onFromDateChange,
  onFromTimeChange,
  onToDateChange,
  onToTimeChange,
  onResultViewChange,
  onSelectedEpisodeIdChange,
  onClearRangeInputs,
  onExport,
}: {
  results: GoldenSetResults | null;
  loading: boolean;
  rangeMode: RangeMode;
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
  rangeError: string;
  resultView: ResultView;
  selectedEpisodeId: string;
  isCsvExporting: boolean;
  onRangeModeChange: (value: RangeMode) => void;
  onFromDateChange: (value: string) => void;
  onFromTimeChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onToTimeChange: (value: string) => void;
  onResultViewChange: (value: ResultView) => void;
  onSelectedEpisodeIdChange: (value: string) => void;
  onClearRangeInputs: () => void;
  onExport: () => Promise<void>;
}) {
  const sortedOperators = useMemo(() => {
    if (!results) return [];
    return [...results.operators].sort((a, b) => {
      const accA = a.total_bits > 0 ? a.match_bits / a.total_bits : -1;
      const accB = b.total_bits > 0 ? b.match_bits / b.total_bits : -1;
      if (accB !== accA) return accB - accA;
      return a.operator.localeCompare(b.operator);
    });
  }, [results]);
  const selectableEpisodes = useMemo(
    () =>
      results?.episodes.filter((ep) => ep.total_attempts > 0 || ep.completed_attempts > 0) ?? [],
    [results]
  );
  const selectedEpisode = selectableEpisodes.find((ep) => ep.episode_id === selectedEpisodeId);
  const isEpisodeModeWithoutSelection = resultView === 'episodes' && !selectedEpisodeId;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-brand-border bg-brand-surface p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="inline-flex w-fit overflow-hidden rounded-lg border border-brand-border bg-brand-dark p-1">
              {(
                [
                  { value: 'all', label: 'All time' },
                  { value: 'range', label: 'Date range' },
                ] satisfies Array<{ value: RangeMode; label: string }>
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onRangeModeChange(option.value)}
                  className={clsx(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition',
                    rangeMode === option.value
                      ? 'bg-brand-orange text-white shadow-sm'
                      : 'text-brand-text-secondary hover:text-white'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <GoldenDateRangePicker
              fromDate={fromDate}
              fromTime={fromTime}
              toDate={toDate}
              toTime={toTime}
              onApply={({ nextFromDate, nextFromTime, nextToDate, nextToTime }) => {
                onFromDateChange(nextFromDate);
                onFromTimeChange(nextFromTime);
                onToDateChange(nextToDate);
                onToTimeChange(nextToTime);
                onRangeModeChange('range');
              }}
              onClear={onClearRangeInputs}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-lg border border-brand-border bg-brand-dark p-1">
              {(
                [
                  { value: 'operators', label: 'Operators' },
                  { value: 'episodes', label: 'Episodes' },
                ] satisfies Array<{ value: ResultView; label: string }>
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onResultViewChange(option.value)}
                  className={clsx(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition',
                    resultView === option.value
                      ? 'bg-brand-surface-highlight text-brand-text-primary shadow-sm'
                      : 'text-brand-text-secondary hover:text-white'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {resultView === 'episodes' && (
              <label className="flex items-center gap-2 text-xs text-brand-text-secondary">
                <span>Episode</span>
                <select
                  value={selectedEpisodeId}
                  onChange={(event) => onSelectedEpisodeIdChange(event.target.value)}
                  disabled={selectableEpisodes.length === 0}
                  className="h-9 min-w-[260px] rounded-lg border border-brand-border bg-brand-dark px-3 text-xs font-mono text-brand-text-primary disabled:opacity-50"
                >
                  {selectableEpisodes.length === 0 ? (
                    <option value="">No episodes in current range</option>
                  ) : (
                    selectableEpisodes.map((episode) => (
                      <option key={episode.episode_id} value={episode.episode_id}>
                        {episode.episode_id}
                      </option>
                    ))
                  )}
                </select>
              </label>
            )}
            <button
              type="button"
              onClick={() => void onExport()}
              disabled={!results || !!rangeError || isEpisodeModeWithoutSelection || isCsvExporting}
              className="flex items-center gap-2 rounded-lg bg-brand-orange px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>
        <div className="mt-2 text-xs text-brand-text-secondary">
          {rangeMode === 'range'
            ? 'Showing attempts whose result timestamp falls in the selected date range.'
            : 'Showing all golden attempts. Date fields are preserved but not applied.'}
        </div>
        {rangeError && <div className="mt-2 text-xs text-red-300">{rangeError}</div>}
      </div>

      {loading || !results ? (
        <div className="text-center py-12 text-brand-text-secondary">
          {loading ? 'Loading…' : rangeError || 'No results yet.'}
        </div>
      ) : isEpisodeModeWithoutSelection ? (
        <div className="rounded-xl border border-brand-border bg-brand-surface p-8 text-center text-sm text-brand-text-secondary">
          No episodes in current range.
        </div>
      ) : (
        <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden">
          <div className="border-b border-brand-border px-5 py-3 text-sm font-semibold text-brand-text-primary">
            {resultView === 'episodes' && selectedEpisode
              ? `Per-operator accuracy for episode ${selectedEpisode.episode_id}`
              : 'Per-operator accuracy'}
          </div>
          {sortedOperators.length === 0 ? (
            <div className="p-8 text-center text-sm text-brand-text-secondary">
              No completed attempts yet.
            </div>
          ) : (
            <div className="divide-y divide-brand-border">
              <div className="grid grid-cols-12 gap-2 bg-brand-surface-highlight/40 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                <div className="col-span-3">Operator</div>
                <div className="col-span-2 text-right">Pending</div>
                <div className="col-span-2 text-right">In progress</div>
                <div className="col-span-2 text-right">Completed</div>
                <div className="col-span-1 text-right">Bits</div>
                <div className="col-span-2 text-right">Accuracy</div>
              </div>
              {sortedOperators.map((op) => {
                const acc = op.total_bits > 0 ? (op.match_bits / op.total_bits) * 100 : null;
                return (
                  <div key={op.operator} className="grid grid-cols-12 gap-2 px-5 py-3 text-sm">
                    <div className="col-span-3 font-medium text-brand-text-primary truncate">
                      {op.operator}
                    </div>
                    <div className="col-span-2 text-right font-mono text-brand-text-secondary">
                      {op.pending}
                    </div>
                    <div className="col-span-2 text-right font-mono text-brand-text-secondary">
                      {op.in_progress}
                    </div>
                    <div className="col-span-2 text-right font-mono text-brand-text-primary">
                      {op.completed}
                    </div>
                    <div className="col-span-1 text-right font-mono text-brand-text-secondary text-xs">
                      {op.match_bits}/{op.total_bits}
                    </div>
                    <div className="col-span-2 text-right font-mono font-bold text-brand-text-primary">
                      {acc != null ? `${acc.toFixed(1)}%` : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GoldenDateRangePicker({
  fromDate,
  fromTime,
  toDate,
  toTime,
  onApply,
  onClear,
}: {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
  onApply: (value: {
    nextFromDate: string;
    nextFromTime: string;
    nextToDate: string;
    nextToTime: string;
  }) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftFromDate, setDraftFromDate] = useState(fromDate);
  const [draftFromTime, setDraftFromTime] = useState(fromTime);
  const [draftToDate, setDraftToDate] = useState(toDate);
  const [draftToTime, setDraftToTime] = useState(toTime);
  const [calendarMonth, setCalendarMonth] = useState(() => parseDateInput(fromDate) ?? new Date());

  useEffect(() => {
    if (!open) return;
    setDraftFromDate(fromDate);
    setDraftFromTime(fromTime || '00:00');
    setDraftToDate(toDate);
    setDraftToTime(toTime || '23:59');
    setCalendarMonth(parseDateInput(fromDate || toDate) ?? new Date());
  }, [fromDate, fromTime, open, toDate, toTime]);

  const monthCells = useMemo(() => {
    const firstOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
    const firstCell = addDays(firstOfMonth, -mondayOffset);
    return Array.from({ length: 42 }, (_, index) => {
      const date = addDays(firstCell, index);
      return {
        date,
        value: toDateInputValue(date),
        inCurrentMonth: date.getMonth() === calendarMonth.getMonth(),
      };
    });
  }, [calendarMonth]);

  const handleSelectDate = (value: string) => {
    if (!draftFromDate || (draftFromDate && draftToDate)) {
      setDraftFromDate(value);
      setDraftToDate('');
      return;
    }
    if (value < draftFromDate) {
      setDraftFromDate(value);
      setDraftToDate('');
      return;
    }
    setDraftToDate(value);
  };

  const applyToday = () => {
    const today = todayDateInputValue();
    setDraftFromDate(today);
    setDraftToDate(today);
    setDraftFromTime('00:00');
    setDraftToTime('23:59');
    setCalendarMonth(parseDateInput(today) ?? new Date());
  };

  const applyLastSevenDays = () => {
    const today = new Date();
    const from = toDateInputValue(addDays(today, -6));
    const to = toDateInputValue(today);
    setDraftFromDate(from);
    setDraftToDate(to);
    setDraftFromTime('00:00');
    setDraftToTime('23:59');
    setCalendarMonth(parseDateInput(to) ?? today);
  };

  const clearDraft = () => {
    setDraftFromDate('');
    setDraftFromTime('');
    setDraftToDate('');
    setDraftToTime('');
    onClear();
  };

  const canApply = !!draftFromDate && !!draftToDate;
  const today = todayDateInputValue();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex min-w-[320px] items-center justify-between gap-3 rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-left text-sm text-brand-text-primary hover:border-brand-text-secondary/50"
      >
        <span className="flex min-w-0 items-center gap-2">
          <CalendarDays className="h-4 w-4 shrink-0 text-brand-orange" />
          <span className="truncate">
            {formatRangeButtonLabel(fromDate, fromTime, toDate, toTime)}
          </span>
        </span>
        <ChevronRight className={clsx('h-4 w-4 shrink-0 transition', open && 'rotate-90')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-[360px] rounded-lg border border-brand-border bg-brand-surface p-4 shadow-2xl">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() =>
                setCalendarMonth(
                  new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1)
                )
              }
              className="rounded-lg border border-brand-border bg-brand-dark p-2 text-brand-text-secondary hover:text-white"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-sm font-semibold text-brand-text-primary">
              {formatCalendarLabel(calendarMonth)}
            </div>
            <button
              type="button"
              onClick={() =>
                setCalendarMonth(
                  new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1)
                )
              }
              className="rounded-lg border border-brand-border bg-brand-dark p-2 text-brand-text-secondary hover:text-white"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-brand-text-secondary">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
              <div key={day} className="py-1">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthCells.map((cell) => {
              const isStart = cell.value === draftFromDate;
              const isEnd = cell.value === draftToDate;
              const isInRange =
                !!draftFromDate &&
                !!draftToDate &&
                cell.value > draftFromDate &&
                cell.value < draftToDate;
              const isToday = cell.value === today;
              return (
                <button
                  key={cell.value}
                  type="button"
                  onClick={() => handleSelectDate(cell.value)}
                  className={clsx(
                    'h-9 rounded-md text-sm transition',
                    !cell.inCurrentMonth && 'text-brand-text-secondary/40',
                    cell.inCurrentMonth && 'text-brand-text-primary',
                    isInRange && 'bg-brand-orange/15 text-white',
                    (isStart || isEnd) && 'bg-brand-orange font-bold text-white',
                    isToday && !isStart && !isEnd && 'ring-1 ring-brand-orange/70',
                    !isInRange && !isStart && !isEnd && 'hover:bg-brand-surface-highlight'
                  )}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                From time
              </span>
              <input
                type="time"
                value={draftFromTime}
                onChange={(event) => setDraftFromTime(event.target.value)}
                className="w-full rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-sm text-brand-text-primary"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                To time
              </span>
              <input
                type="time"
                value={draftToTime}
                onChange={(event) => setDraftToTime(event.target.value)}
                className="w-full rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-sm text-brand-text-primary"
              />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={applyToday}
                className="rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs text-brand-text-secondary hover:text-white"
              >
                Today
              </button>
              <button
                type="button"
                onClick={applyLastSevenDays}
                className="rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs text-brand-text-secondary hover:text-white"
              >
                Last 7 days
              </button>
              <button
                type="button"
                onClick={clearDraft}
                className="rounded-lg border border-brand-border bg-transparent px-3 py-2 text-xs text-brand-text-secondary hover:text-white"
              >
                Clear
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!canApply) return;
                onApply({
                  nextFromDate: draftFromDate,
                  nextFromTime: draftFromTime,
                  nextToDate: draftToDate,
                  nextToTime: draftToTime,
                });
                setOpen(false);
              }}
              disabled={!canApply}
              className="rounded-lg bg-brand-orange px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
        {label}
      </div>
      <div className="mt-1 text-xl font-bold font-mono text-brand-text-primary">{value}</div>
    </div>
  );
}
